import React from 'react'
import styled, { css } from 'styled-components'
import { color } from '../styles'
import { SPACING, TRANSITION_TIME_MD_MS } from '../styles/mixins/constants'
import { text, textLineHeights, textSizes } from '../styles/mixins/text'
import { insertTestingSelector, TestingKey } from '../util/testing'
import { IconKey, Icon, IconColor, IconSize } from './Icon'
import { Spacer } from './Spacer'
import { ACTION_OPACITY } from '../styles/mixins/button'

export type ControlType =
    | 'primary'
    | 'accent'
    | 'text'
    | 'text_accent'
    | 'text_danger'
    | 'danger'
    | 'danger_outlined'

export type ControlSize = 'small' | 'medium' | 'large' | 'super'

type Props = {
    type?: ControlType
    size?: ControlSize
    iconKey?: IconKey
    disabled?: boolean
    isIconOnly?: boolean
    label?: string | JSX.Element
    uppercase?: boolean
    className?: string
    style?: React.CSSProperties
    testingKeys?: [TestingKey] | [TestingKey, string]
    isLoading?: boolean
    iconPosition?: 'right' | 'left'
    onClick: () => void
}

export const Button = (props: Props): JSX.Element => {
    const {
        type = 'text',
        size = 'small',
        disabled,
        iconKey: requestedIconKey,
        label,
        isIconOnly,
        iconPosition = 'left',
        uppercase,
        className,
        style,
        testingKeys = ['ControlButton'],
        isLoading,
        onClick,
    } = props
    const getControlIconColor = (): IconColor | undefined => {
        if (disabled) return 'secondary'
        switch (type) {
            case 'danger':
            case 'accent':
                return 'inverted'
            case 'danger_outlined':
                return 'danger'
            case 'text':
            case 'primary':
                return 'primary'
            case 'text_accent':
                return 'activity'
            case 'text_danger':
                return 'danger'
            default:
                return undefined
        }
    }

    const getControlIconSize = (): IconSize => {
        switch (size) {
            case 'super':
                return 8
            case 'large':
                return 3
            case 'medium':
                return 2
            case 'small':
                return 1
            default:
                return 1
        }
    }

    const iconKey = isLoading ? 'Loading' : requestedIconKey

    return (
        <BtnContent
            {...insertTestingSelector(...testingKeys)}
            className={className}
            style={style}
            buttonType={type}
            type="button"
            disabled={Boolean(disabled || isLoading)}
            isIcon={Boolean(isIconOnly)}
            isUppercase={Boolean(uppercase)}
            size={size}
            onClick={() => {
                if (disabled) return
                onClick()
            }}
        >
            {iconKey && iconPosition === 'left' && (
                <>
                    <Icon
                        iconKey={iconKey}
                        size={getControlIconSize()}
                        color={getControlIconColor()}
                    />
                    {!isIconOnly && <Spacer size={size === 'small' ? 1 : 2} />}
                </>
            )}
            {!isIconOnly ? <Label>{label}</Label> : null}
            {iconKey && iconPosition === 'right' && (
                <>
                    {!isIconOnly && <Spacer size={size === 'small' ? 1 : 2} />}
                    <Icon
                        iconKey={iconKey}
                        size={getControlIconSize()}
                        color={getControlIconColor()}
                    />
                </>
            )}
        </BtnContent>
    )
}

const textStyle = css`
    ${(p: { size: ControlSize; disabled?: boolean }) =>
        text(p.size === 'small' ? 'paragraph' : 'title', 'primary', 'text', 'bold')}
    background-color: transparent;
    border: 1px solid transparent;
    padding: 0px;
    border-width: 0px;
    &&:active {
        opacity: 1;
        border-width: 0px;
        background-color: transparent;
    }
    &&:hover {
        opacity: ${ACTION_OPACITY};
        border-width: 0px;
        background-color: transparent;
    }
    ${(p) =>
        p.disabled
            ? css`
                  &&,
                  &&:hover,
                  &&:active {
                      border-width: 0px;
                      background-color: transparent;
                      opacity: ${ACTION_OPACITY};
                  }
              `
            : ''}
`

const textStyleAccent = css`
    ${textStyle}
    color: ${color.brand.primary};
`

const textStyleDanger = css`
    ${textStyle}
    color: ${color.status.danger};
`

const primaryStyle = css`
    ${text('paragraph', 'secondary')}
    background-color: ${color.background.control};
    border: 1px solid ${color.border.primary};
    &:active {
        opacity: ${ACTION_OPACITY};
    }
    &:hover,
    &:active {
        border: 1px solid ${color.brand.primary};
        color: ${color.brand.primary};
        svg {
            fill: ${color.brand.primary};
        }
    }
`

const dangerStyle = css`
    ${text('paragraph', 'inverted')}
    background-color: ${color.status.danger};
    border: 1px solid ${color.status.danger};
    &:hover {
        opacity: ${ACTION_OPACITY};
    }
    &:active {
        opacity: 1;
    }
`

const dangerOutlineStyle = css`
    ${primaryStyle}
    &,
  &:hover,
  &:active {
        border-color: ${color.status.danger};
        color: ${color.status.danger};
        svg {
            fill: ${color.status.danger};
        }
    }
    &:hover,
    &:hover svg {
        opacity: ${ACTION_OPACITY};
    }
    &:active,
    &:active svg {
        opacity: 1;
    }
`

const accentStyle = css`
    ${text('paragraph', 'inverted')}
    background-color: ${color.brand.primary};
    border: 1px solid ${color.brand.primary};
    &:hover {
        opacity: ${ACTION_OPACITY};
    }
    &:active {
        opacity: 1;
    }
`

const disabledStyle = css`
    &,
    &:hover,
    &:active {
        color: ${color.text.secondary};
        border: 1px solid ${color.border.muted};
        background-color: ${color.background.muted};
        cursor: not-allowed;
        opacity: 1;
        svg {
            fill: ${color.icon.secondary};
        }
    }
`

const buttonStandardSizingStyle = css`
    font-size: ${textSizes.paragraph};
    line-height: ${textLineHeights.paragraph};
`

const buttonSmallStyle = css`
    ${buttonStandardSizingStyle}
    padding: 0 ${SPACING[2]};
`

const buttonMediumStyle = css`
    ${buttonStandardSizingStyle}
    padding: ${SPACING[1]} ${SPACING[4]};
`

const buttonLargeStyle = css`
    font-size: ${textSizes.title};
    line-height: ${textLineHeights.title};
    padding: ${SPACING[2]} ${SPACING[4]};
`

const iconOnlyStyle = css`
    padding: ${SPACING[2]} ${SPACING[2]};
    align-items: center;
    border-radius: 100%;
`

const BtnContent = styled.button<{
    buttonType: ControlType
    size: ControlSize
    isIcon: boolean
    isUppercase: boolean
}>`
    border: 0px;
    outline: none;
    border-radius: 2px;
    cursor: pointer;
    display: flex;
    flex-flow: row nowrap;
    flex: 0 1 auto;
    min-width: 0;
    min-height: 0;
    align-items: center;
    justify-content: center;
    &,
    & svg {
        transition: all ${TRANSITION_TIME_MD_MS}ms;
    }
    ${(p) =>
        p.isUppercase &&
        `
    text-transform: uppercase;
  `}
    ${(p) => {
        switch (p.size) {
            case 'large':
                return buttonLargeStyle
            case 'medium':
                return buttonMediumStyle
            default:
                return buttonSmallStyle
        }
    }}
  ${(p) => {
        switch (p.buttonType) {
            case 'accent':
                return accentStyle
            case 'danger':
                return dangerStyle
            case 'danger_outlined':
                return dangerOutlineStyle
            case 'text':
                return textStyle
            case 'text_accent':
                return textStyleAccent
            case 'text_danger':
                return textStyleDanger
            default:
                return primaryStyle
        }
    }}

  ${(p) => (p.disabled ? disabledStyle : '')}

  ${(p) => (p.isIcon ? iconOnlyStyle : '')}
`

const Label = styled.div``
